/**
 * KAN-837: часы, когда менеджеру можно звонить (work_hours менеджера).
 * Дни недели 1 = Пн .. 7 = Вс, время "HH:MM" в часовом поясе организации.
 * Здесь черновик редактора, проверка перед сохранением, тело запроса и
 * короткая строка для показа. Пустой объект {} значит "не задано".
 */
import type { WorkHours } from '@/types/manager';

export const WEEKDAYS: ReadonlyArray<{ day: number; short: string; full: string }> = [
  { day: 1, short: 'Пн', full: 'Понедельник' },
  { day: 2, short: 'Вт', full: 'Вторник' },
  { day: 3, short: 'Ср', full: 'Среда' },
  { day: 4, short: 'Чт', full: 'Четверг' },
  { day: 5, short: 'Пт', full: 'Пятница' },
  { day: 6, short: 'Сб', full: 'Суббота' },
  { day: 7, short: 'Вс', full: 'Воскресенье' },
];

/** Время, которое подставляется, когда выбран первый день, а время пустое. */
export const DEFAULT_WORK_START = '09:00';
export const DEFAULT_WORK_END = '18:00';

export const WORK_HOURS_LABEL = 'Когда мне можно звонить';
export const WORK_HOURS_NO_DAYS_ERROR = 'Выберите хотя бы один день';
export const WORK_HOURS_NO_TIME_ERROR = 'Укажите время с и до';
export const WORK_HOURS_ORDER_ERROR = 'Время "до" должно быть позже времени "с"';
export const WORK_HOURS_EMPTY_TEXT = 'Не указано';

/** Черновик редактора: всегда все три поля. */
export interface WorkHoursDraft {
  days: number[];
  start: string;
  end: string;
}

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function emptyWorkHoursDraft(): WorkHoursDraft {
  return { days: [], start: '', end: '' };
}

/** Черновик из ответа backend ({} или неполный объект дают пустые поля). */
export function workHoursToDraft(value: WorkHours | null | undefined): WorkHoursDraft {
  const days = (value?.days ?? []).filter((d) => Number.isInteger(d) && d >= 1 && d <= 7);
  return {
    days: Array.from(new Set(days)).sort((a, b) => a - b),
    start: value?.start ?? '',
    end: value?.end ?? '',
  };
}

/**
 * Включить или выключить день. Первый выбранный день при пустом времени
 * подставляет 09:00-18:00, чтобы не заставлять вводить время с нуля.
 */
export function toggleWorkDay(draft: WorkHoursDraft, day: number): WorkHoursDraft {
  const on = draft.days.includes(day);
  const days = on ? draft.days.filter((d) => d !== day) : [...draft.days, day].sort((a, b) => a - b);
  if (!on && draft.days.length === 0 && !draft.start && !draft.end) {
    return { days, start: DEFAULT_WORK_START, end: DEFAULT_WORK_END };
  }
  return { ...draft, days };
}

function isBlank(draft: WorkHoursDraft): boolean {
  return draft.days.length === 0 && !draft.start && !draft.end;
}

/** Ошибка черновика или null. Полностью пустой черновик допустим (снять часы). */
export function validateWorkHours(draft: WorkHoursDraft): string | null {
  if (isBlank(draft)) return null;
  if (draft.days.length === 0) return WORK_HOURS_NO_DAYS_ERROR;
  if (!TIME_RE.test(draft.start) || !TIME_RE.test(draft.end)) return WORK_HOURS_NO_TIME_ERROR;
  if (draft.end <= draft.start) return WORK_HOURS_ORDER_ERROR;
  return null;
}

/** Тело для backend: пустой черновик даёт {} (не задано). */
export function draftToWorkHours(draft: WorkHoursDraft): WorkHours {
  if (isBlank(draft)) return {};
  return { days: [...draft.days], start: draft.start, end: draft.end };
}

/** Дни подряд склеиваются: [1,2,3,4,5] -> "Пн-Пт", [1,3] -> "Пн, Ср". */
function formatDays(days: number[]): string {
  const sorted = Array.from(new Set(days)).sort((a, b) => a - b);
  const groups: number[][] = [];
  for (const day of sorted) {
    const last = groups[groups.length - 1];
    if (last && day === last[last.length - 1] + 1) last.push(day);
    else groups.push([day]);
  }
  const short = (d: number) => WEEKDAYS[d - 1]?.short ?? String(d);
  return groups
    .map((g) => (g.length >= 3 ? `${short(g[0])}-${short(g[g.length - 1])}` : g.map(short).join(', ')))
    .join(', ');
}

/** Часы заданы полностью: есть дни, время с и до ({} или неполный объект: нет). */
export function hasWorkHours(value: WorkHours | null | undefined): boolean {
  const draft = workHoursToDraft(value);
  return draft.days.length > 0 && !!draft.start && !!draft.end;
}

/** Строка для показа: "Пн-Пт, 09:00-18:00"; не задано: "Не указано". */
export function formatWorkHours(value: WorkHours | null | undefined): string {
  const draft = workHoursToDraft(value);
  if (draft.days.length === 0 || !draft.start || !draft.end) return WORK_HOURS_EMPTY_TEXT;
  return `${formatDays(draft.days)}, ${draft.start}-${draft.end}`;
}

/** Подсказка о часовом поясе организации; пустой пояс: без подсказки. */
export function timezoneHint(timezone: string | null | undefined): string {
  return timezone ? `Время по часовому поясу организации: ${timezone}` : '';
}

/**
 * Первое сообщение об ошибке work_hours из тела 400: строка, список строк
 * или вложенный объект {days: ["..."]}.
 */
export function workHoursErrorMessage(value: unknown): string | null {
  if (typeof value === 'string') return value || null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const msg = workHoursErrorMessage(item);
      if (msg) return msg;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value as Record<string, unknown>)) {
      const msg = workHoursErrorMessage(item);
      if (msg) return msg;
    }
  }
  return null;
}
